import { Link } from '@tanstack/react-router';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { MailIcon, PhoneIcon, MapPinIcon, CalendarIcon, BriefcaseIcon, ArrowLeftIcon, PlusIcon } from 'lucide-react';
import type { Customer } from '@repo/types';
import { ToneBadge } from './_shared';
import { getJobSearchStatusTone, formatCareerLevel, humanize, getCurrentRole } from './utils';

export function CustomerHeader({
  customer,
  initials,
  joined,
  onAssignJobClick,
}: {
  customer: Customer;
  initials: string;
  joined: string | null;
  onAssignJobClick: () => void;
}) {
  const currentRole = getCurrentRole(customer);
  return (
    <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
        <Avatar className="h-16 w-16 border sm:h-20 sm:w-20">
          <AvatarFallback className="bg-primary/10 text-xl font-bold text-primary">{initials}</AvatarFallback>
        </Avatar>

        <div className="space-y-2.5">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-bold tracking-tight">{customer.name}</h1>
            {customer.jobSearchStatus && (
              <ToneBadge tone={getJobSearchStatusTone(customer.jobSearchStatus)}>
                {humanize(customer.jobSearchStatus)}
              </ToneBadge>
            )}
            {customer.careerLevel && <ToneBadge tone="neutral">{formatCareerLevel(customer.careerLevel)}</ToneBadge>}
          </div>

          {(currentRole.title || currentRole.company) && (
            <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <BriefcaseIcon className="h-4 w-4 shrink-0" aria-hidden />
              <span className="font-medium text-foreground">{currentRole.title}</span>
              {currentRole.company && <span>at {currentRole.company}</span>}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm text-muted-foreground">
            {customer.email && (
              <a
                href={`mailto:${customer.email}`}
                className="inline-flex items-center gap-1.5 transition-colors hover:text-foreground"
              >
                <MailIcon className="h-4 w-4" aria-hidden />
                <span className="break-all">{customer.email}</span>
              </a>
            )}
            {customer.phone && (
              <a
                href={`tel:${customer.phone}`}
                className="inline-flex items-center gap-1.5 transition-colors hover:text-foreground"
              >
                <PhoneIcon className="h-4 w-4" aria-hidden />
                {customer.phone}
              </a>
            )}
            {customer.location && (
              <span className="inline-flex items-center gap-1.5">
                <MapPinIcon className="h-4 w-4" aria-hidden />
                {customer.location}
              </span>
            )}
            {joined && (
              <span className="inline-flex items-center gap-1.5">
                <CalendarIcon className="h-4 w-4" aria-hidden />
                Joined {joined}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        <Button onClick={onAssignJobClick} size="sm" className="gap-2">
          <PlusIcon className="h-4 w-4" aria-hidden />
          Assign Job
        </Button>
        <Link to="/admin/customers">
          <Button variant="outline" size="sm" className="gap-2">
            <ArrowLeftIcon className="h-4 w-4" aria-hidden />
            <span className="hidden sm:inline">Back</span>
          </Button>
        </Link>
      </div>
    </div>
  );
}
